'use client'

import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../cn'
import { SurfaceCloseButton } from './icon-button-close'

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/10 supports-[backdrop-filter]:backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out focus:outline-none focus-visible:outline-none',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-dvh sm:h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        right:
          'inset-y-0 right-0 h-dvh sm:h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      },
      size: {
        default: '',
        sm: '',
        md: '',
        lg: '',
        xl: '',
        '2xl': '',
        full: '',
      },
    },
    compoundVariants: [
      { side: ['left', 'right'], size: 'default', className: 'w-full sm:w-3/4 sm:max-w-sm' },
      { side: ['left', 'right'], size: 'sm', className: 'w-full sm:w-3/4 sm:max-w-xs' },
      { side: ['left', 'right'], size: 'md', className: 'w-full sm:w-3/4 sm:max-w-md' },
      { side: ['left', 'right'], size: 'lg', className: 'w-full sm:w-3/4 sm:max-w-lg' },
      { side: ['left', 'right'], size: 'xl', className: 'w-full sm:w-3/4 sm:max-w-xl' },
      { side: ['left', 'right'], size: '2xl', className: 'w-full sm:w-3/4 sm:max-w-2xl' },
      { side: ['left', 'right'], size: 'full', className: 'w-full' },
      { side: ['top', 'bottom'], size: 'full', className: 'h-full' },
    ],
    defaultVariants: {
      side: 'right',
      size: 'default',
    },
  },
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  /** Hide the default close button (useful when providing custom close UI) */
  hideCloseButton?: boolean
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  (
    {
      side = 'right',
      size = 'default',
      className,
      children,
      hideCloseButton,
      onEscapeKeyDown,
      onOpenAutoFocus,
      ...props
    },
    ref,
  ) => {
    const contentRef = React.useRef<React.ElementRef<typeof SheetPrimitive.Content> | null>(null)

    const setRef = React.useCallback(
      (node: React.ElementRef<typeof SheetPrimitive.Content> | null) => {
        contentRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ;(ref as React.MutableRefObject<React.ElementRef<typeof SheetPrimitive.Content> | null>).current = node
        }
      },
      [ref],
    )

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={setRef}
          onEscapeKeyDown={(e) => {
            e.stopPropagation()
            onEscapeKeyDown?.(e)
          }}
          onOpenAutoFocus={(e) => {
            onOpenAutoFocus?.(e)
            if (e.defaultPrevented) {
              return
            }
            e.preventDefault()

            const focusContent = () => contentRef.current?.focus({ preventScroll: true })
            if (typeof window === 'undefined') {
              focusContent()
            } else {
              window.requestAnimationFrame(focusContent)
            }
          }}
          className={cn(sheetVariants({ side, size }), className)}
          {...props}>
          {!hideCloseButton && (
            <SheetPrimitive.Close asChild>
              <SurfaceCloseButton className="absolute right-3 top-3" />
            </SheetPrimitive.Close>
          )}
          {children}
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  },
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
